import React, { useState, useMemo, useEffect, useCallback } from 'react'
import { Link } from 'react-router-dom'
import './PredictionPage.scss'
import logoImage_1 from '../../assets/White PNG 1.png'
import logoImage_2 from '../../assets/Black PNG 1.png'
import CalculationDetails from './CalculationDetailsComponent'

import { Line } from 'react-chartjs-2'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js'

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler)

interface PredictionPageProps {
  theme: string
}

interface TableRow {
  netProfit: number
  probability: number
  wins: number
  losses: number
}

interface MonteCarloChartData {
  days: number[]
  p5: number[]
  p50: number[]
  p95: number[]
  finalP5: number
  finalP50: number
  finalP95: number
  chartOptions: {
    yAxisMin: number
    yAxisMax: number
  }
}

interface RecommendedPlan {
  rank: number
  p: number
  RR: number
  R: number
  m: number
  alpha: number
  probHit: number
  retMed: number
  ret5: number
  ret95: number
  score: number
}

const toEnglishDigits = (str: string): string => {
  if (!str) return str
  return str
    .replace(/[۰-۹]/g, d => '۰۱۲۳۴۵۶۷۸۹'.indexOf(d).toString())
    .replace(/[٠-٩]/g, d => '٠١٢٣٤٥٦٧٨٩'.indexOf(d).toString())
}

function simulateBandsCompounding({ V0, p, R, RR, m, D, feeUSD, alpha = 0.75, paths = 2000, seed = 42 }: any) {
  const fStar = p - (1 - p) / RR
  const f = Math.min(Math.max(fStar, 0), 1) * alpha

  const Mw = 1 + f * R * RR
  const Ml = 1 - f * R
  if (Mw <= 0 || Ml <= 0) return null

  let state = seed >>> 0
  const rand = () => ((state = (1664525 * state + 1013904223) >>> 0), state / 2 ** 32)

  const p5: number[] = []
  const p50: number[] = []
  const p95: number[] = []
  const days: number[] = []

  const currentValues = new Float64Array(paths).fill(V0)
  const eps = 1e-6

  for (let d = 1; d <= D; d++) {
    for (let s = 0; s < paths; s++) {
      let V = currentValues[s]
      for (let t = 0; t < m; t++) {
        const win = rand() < p
        V *= win ? Mw : Ml
        V -= feeUSD
        if (V < eps) V = eps
      }
      currentValues[s] = V
    }

    const sorted = Float64Array.from(currentValues).sort()
    p5.push(sorted[Math.floor(paths * 0.05)])
    p50.push(sorted[Math.floor(paths * 0.5)])
    p95.push(sorted[Math.floor(paths * 0.95)])
    days.push(d)
  }

  return { days, p5, p50, p95 }
}

const PredictionPage: React.FC<PredictionPageProps> = ({ theme }) => {
  const [dailyRisk, setDailyRisk] = useState<string>('1')
  const [riskPerTrade, setRiskPerTrade] = useState<string>('0.5')
  const [rrRatio, setRrRatio] = useState<string>('2')
  const [tableData, setTableData] = useState<TableRow[] | null>(null)
  const [tableError, setTableError] = useState<string>('')
  const [projectionDays, setProjectionDays] = useState<string>('30')
  const [initialCapital, setInitialCapital] = useState<string>('1000')
  const [winProbability, setWinProbability] = useState<string>('0.5')
  const [feePerTrade, setFeePerTrade] = useState<string>('0.3')
  const [kellyAlpha, setKellyAlpha] = useState<string>('1.0')
  const [chartSimulationData, setChartSimulationData] = useState<MonteCarloChartData | null>(null)
  const [chartError, setChartError] = useState<string>('')

  const [recommenderDays, setRecommenderDays] = useState<string>('90')
  const [targetProfitPercent, setTargetProfitPercent] = useState<string>('8')
  const [riskLevel, setRiskLevel] = useState<'low' | 'med' | 'high'>('med')
  const [isRecommending, setIsRecommending] = useState<boolean>(false)
  const [recommendedPlans, setRecommendedPlans] = useState<RecommendedPlan[]>([])
  const [recommendationError, setRecommendationError] = useState<string>('')

  const handleInputChange =
    (setter: React.Dispatch<React.SetStateAction<string>>) => (e: React.ChangeEvent<HTMLInputElement>) => {
      setter(toEnglishDigits(e.target.value))
    }

  const numTrades = useMemo(() => {
    const riskDay = parseFloat(dailyRisk)
    const riskTrade = parseFloat(riskPerTrade)
    if (riskDay > 0 && riskTrade > 0) return Math.floor(riskDay / riskTrade)
    return 0
  }, [dailyRisk, riskPerTrade])

  const factorial = (n: number): number => {
    if (n < 0) return 0
    if (n === 0 || n === 1) return 1
    let result = 1
    for (let i = 2; i <= n; i++) result *= i
    return result
  }
  const binomialCoefficient = useCallback((n: number, k: number): number => {
    if (k < 0 || k > n) return 0
    return factorial(n) / (factorial(k) * factorial(n - k))
  }, [])

  const handleGenerateTable = useCallback(() => {
    const riskDayNum = parseFloat(dailyRisk)
    const riskPerTradeNum = parseFloat(riskPerTrade)
    const rrRatioNum = parseFloat(rrRatio)

    if ([riskDayNum, riskPerTradeNum, rrRatioNum].some(x => isNaN(x) || x <= 0)) {
      setTableError('لطفاً مقادیر معتبر وارد کنید.')
      setTableData(null)
      return
    }
    if (numTrades < 1) {
      setTableError('ریسک روزانه باید بیشتر از ریسک هر ترید باشد.')
      setTableData(null)
      return
    }
    setTableError('')
    const data: TableRow[] = []
    for (let wins = 0; wins <= numTrades; wins++) {
      const losses = numTrades - wins
      const netProfit = wins * rrRatioNum * riskPerTradeNum - losses * riskPerTradeNum
      const probability = binomialCoefficient(numTrades, wins) * Math.pow(0.5, wins) * Math.pow(0.5, losses) * 100
      data.push({ netProfit, probability, wins, losses })
    }
    setTableData(data)
  }, [dailyRisk, riskPerTrade, rrRatio, numTrades, binomialCoefficient])

  const handleGenerateChart = useCallback(() => {
    try {
      setChartError('')
      const V0 = parseFloat(initialCapital)
      const p = parseFloat(winProbability)
      const R = parseFloat(riskPerTrade) / 100
      const RR = parseFloat(rrRatio)
      const m = numTrades
      const D = parseInt(projectionDays)
      const feeUSD = parseFloat(feePerTrade)
      const alpha = parseFloat(kellyAlpha)

      if ([V0, p, R, RR, m, D, feeUSD, alpha].some(isNaN) || m < 1) return

      const res = simulateBandsCompounding({ V0, p, R, RR, m, D, feeUSD, alpha, paths: 2000 })
      if (!res) throw new Error('پارامترها نامعتبر هستند.')

      const toPct = (arr: number[]) => arr.map(v => ((v - V0) / V0) * 100)
      const p5_percent = toPct(res.p5)
      const p50_percent = toPct(res.p50)
      const p95_percent = toPct(res.p95)

      const allData = [...p5_percent, ...p50_percent, ...p95_percent]
      const minV = Math.min(...allData)
      const maxV = Math.max(...allData)
      const lastIdx = p5_percent.length - 1

      setChartSimulationData({
        days: res.days,
        p5: p5_percent,
        p50: p50_percent,
        p95: p95_percent,
        finalP5: p5_percent[lastIdx],
        finalP50: p50_percent[lastIdx],
        finalP95: p95_percent[lastIdx],
        chartOptions: { yAxisMin: minV - 5, yAxisMax: maxV + 5 }
      })
    } catch (e: any) {
      setChartError(e.message)
    }
  }, [initialCapital, winProbability, riskPerTrade, rrRatio, numTrades, projectionDays, feePerTrade, kellyAlpha])

  const handleCalculateAll = () => {
    handleGenerateTable()
    handleGenerateChart()
  }

  useEffect(() => {
    handleCalculateAll()
  }, [])

  const lineChartData = useMemo(() => {
    if (!chartSimulationData) return null
    return {
      labels: chartSimulationData.days,
      datasets: [
        {
          label: 'بدبینانه (5%)',
          data: chartSimulationData.p5,
          borderColor: '#ef4444',
          borderWidth: 1,
          pointRadius: 0,
          tension: 0.1
        },
        {
          label: 'نرمال (50%)',
          data: chartSimulationData.p50,
          borderColor: '#3b82f6',
          borderWidth: 2,
          pointRadius: 0,
          tension: 0.1
        },
        {
          label: 'خوشبینانه (95%)',
          data: chartSimulationData.p95,
          borderColor: '#22c55e',
          borderWidth: 1,
          pointRadius: 0,
          tension: 0.1
        }
      ]
    }
  }, [chartSimulationData])

  const handleRunRecommendation = useCallback(async () => {
    setIsRecommending(true)
    setRecommendationError('')
    setRecommendedPlans([])

    await new Promise(r => setTimeout(r, 50))

    try {
      const V0 = parseFloat(initialCapital)
      const D = parseInt(recommenderDays)
      const targetPct = parseFloat(targetProfitPercent) / 100

      if (isNaN(V0) || isNaN(D) || isNaN(targetPct)) {
        throw new Error('لطفاً سرمایه، تعداد روز و هدف سود را به درستی وارد کنید.')
      }

      const FIXED_ALPHA = 0.75
      const FIXED_FEE_USD = 0.01
      const paths = 1200
      const targetFactor = 1 + targetPct

      let mRange: number[]
      let RRange: number[]

      if (riskLevel === 'low') {
        mRange = [2, 3, 4]
        RRange = [0.005, 0.0075, 0.01]
      } else if (riskLevel === 'high') {
        mRange = [4, 6, 8]
        RRange = [0.015, 0.0175, 0.02]
      } else {
        mRange = [3, 4, 5, 6]
        RRange = [0.01, 0.0125, 0.015]
      }

      const alphaRange = [FIXED_ALPHA]
      const pList = [0.3, 0.35, 0.4, 0.45]
      const RR_list = [2.5, 3, 3.5, 4]

      const results: RecommendedPlan[] = []
      const seedBase = 9999

      const makeRand = (seed: number) => {
        let state = seed >>> 0
        return () => {
          state = (1664525 * state + 1013904223) >>> 0
          return state / 2 ** 32
        }
      }

      const quantile = (sortedArr: Float64Array, q: number) => {
        const n = sortedArr.length
        if (n === 0) return NaN
        const idx = Math.floor((n - 1) * q)
        return sortedArr[idx]
      }

      for (const p of pList) {
        for (const RR of RR_list) {
          for (const R of RRange) {
            for (const alpha of alphaRange) {
              for (const m of mRange) {
                const fStar = p - (1 - p) / RR
                if (fStar <= 0) continue

                const f = Math.min(Math.max(fStar, 0), 1) * alpha
                const Mw = 1 + f * R * RR
                const Ml = 1 - f * R
                if (Mw <= 0 || Ml <= 0) continue

                const rand = makeRand(seedBase + Math.floor(100000 * p + 1000 * RR + 10000 * R + 10 * m))
                const finals = new Float64Array(paths)

                for (let s = 0; s < paths; s++) {
                  let V = V0
                  for (let d = 0; d < D; d++) {
                    for (let t = 0; t < m; t++) {
                      const win = rand() < p
                      V *= win ? Mw : Ml
                      V -= FIXED_FEE_USD
                      if (V < 1e-6) V = 1e-6
                    }
                  }
                  finals[s] = V
                }

                const arr = Array.from(finals).sort((a, b) => a - b)
                const floatArr = Float64Array.from(arr)

                const medV = quantile(floatArr, 0.5)
                const v5 = quantile(floatArr, 0.05)
                const v95 = quantile(floatArr, 0.95)

                const factorMed = medV / V0
                const factor5 = v5 / V0
                const factor95 = v95 / V0

                const retMed = factorMed - 1
                const ret5 = factor5 - 1
                const ret95 = factor95 - 1

                const hits = arr.filter(v => v >= V0 * targetFactor).length
                const probHit = hits / paths

                if (ret5 < -0.05) continue

                const w1 = 0.6,
                  w2 = 0.3,
                  w3 = 0.1
                const edge = Math.max(0, retMed - targetPct)
                const score = w1 * probHit + w2 * edge + w3 * ret5

                results.push({
                  rank: 0,
                  p,
                  RR,
                  R,
                  m,
                  alpha,
                  probHit,
                  retMed,
                  ret5,
                  ret95,
                  score
                })
              }
            }
          }
        }
      }

      results.sort((a, b) => b.score - a.score)
      const top5 = results.slice(0, 5).map((x, i) => ({ ...x, rank: i + 1 }))

      if (top5.length === 0) {
        throw new Error(
          'با این هدف سود و این سطح ریسک، پلن مناسبی پیدا نشد. لطفاً هدف را کمتر یا سطح ریسک را بالاتر انتخاب کنید.'
        )
      }
      setRecommendedPlans(top5)
    } catch (e: any) {
      setRecommendationError(e.message)
    } finally {
      setIsRecommending(false)
    }
  }, [initialCapital, recommenderDays, targetProfitPercent, riskLevel])

  return (
    <div className={`prediction-page ${theme}`}>
      <header className='header'>
        <nav className='nav-container'>
          <div className='logo'>
            <Link to={`/?theme=${theme}`}>
              <img src={theme === 'dark' ? logoImage_1 : logoImage_2} alt='Logo' />
            </Link>
          </div>
        </nav>
      </header>

      <div className='prediction-page-header'>
        <h2>ماشین‌حساب و پیش‌بینی استراتژی</h2>
        <p>ابزاری برای تحلیل ریسک، شبیه‌سازی آینده حساب و یافتن پلن معاملاتی بهینه</p>
      </div>

      <div className='prediction-form-container'>
        <div className='input-grid-full'>
          <div className='input-group'>
            <label>سرمایه اولیه ($):</label>
            <input
              type='text'
              inputMode='decimal'
              value={initialCapital}
              onChange={handleInputChange(setInitialCapital)}
            />
          </div>
          <div className='input-group'>
            <label>ریسک روزانه (%):</label>
            <input type='text' inputMode='decimal' value={dailyRisk} onChange={handleInputChange(setDailyRisk)} />
          </div>
          <div className='input-group'>
            <label>ریسک هر ترید (%):</label>
            <input type='text' inputMode='decimal' value={riskPerTrade} onChange={handleInputChange(setRiskPerTrade)} />
          </div>
          <div className='input-group'>
            <label>ریسک به ریوارد (RR):</label>
            <input type='text' inputMode='decimal' value={rrRatio} onChange={handleInputChange(setRrRatio)} />
          </div>
          <div className='input-group'>
            <label>تعداد ترید (m):</label>
            <input type='text' value={numTrades} readOnly className='readonly' />
          </div>
          <div className='input-group'>
            <label>WinRate (نمودار):</label>
            <input
              type='text'
              inputMode='decimal'
              value={winProbability}
              onChange={handleInputChange(setWinProbability)}
            />
          </div>
        </div>
        <p style={{ color: 'orange', fontSize: '0.9em', marginBottom: '8PX' }}>
          ⚠️ نکته: نرخ برد را به صورت اعشاری وارد کنید (مثلاً: 50% = 0.5)
        </p>
        <button className='generate-btn' onClick={handleCalculateAll}>
          محاسبه جدول و نمودار
        </button>
        {tableError && <p className='error-message'>{tableError}</p>}
      </div>

      {tableData && (
        <div className='prediction-table-container'>
          <table>
            <thead>
              <tr>
                <th>سود/زیان</th>
                <th>احتمال</th>
                <th>وضعیت تریدها</th>
              </tr>
            </thead>
            <tbody>
              {tableData.map((r, i) => (
                <tr key={i}>
                  <td style={{ direction: 'ltr', color: r.netProfit >= 0 ? '#00b4d8' : '#f43f5e' }}>
                    {r.netProfit.toFixed(2)}%
                  </td>
                  <td>{r.probability.toFixed(1)}%</td>
                  <td className='trades-cell'>
                    <span style={{ color: '#00b4d8' }}>{'✅'.repeat(r.wins)}</span>
                    <span style={{ color: '#f43f5e' }}>{'❌'.repeat(r.losses)}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {chartSimulationData && (
        <div className='projection-chart-container'>
          <div className='chart-controls'>
            <div className='input-group'>
              <label>روزها (D):</label>
              <input
                type='text'
                inputMode='numeric'
                value={projectionDays}
                onChange={handleInputChange(setProjectionDays)}
              />
            </div>
            <button className='mini-btn' onClick={handleGenerateChart}>
              بروزرسانی نمودار
            </button>
          </div>

          <div className='chart-wrapper'>
            <Line
              data={lineChartData!}
              options={{ maintainAspectRatio: false, scales: { x: { grid: { display: false } } } }}
            />
          </div>

          <div className='chart-summary-section'>
            <h4 className='summary-title'>خلاصه نتایج پس از {projectionDays} روز</h4>
            <p className='summary-subtitle'>بر اساس شبیه‌سازی مونت‌کارلو (تبدیل شده به درصد رشد)</p>

            <div className='summary-cards'>
              <div className='summary-card bad'>
                <div className='card-header'>
                  <span>سناریوی بدبینانه (5%)</span>
                  <span className='percent-badge'>5% بدترین نتایج</span>
                </div>
                <div className='card-value'>
                  <span className='trend-icon'>📉</span>
                  {chartSimulationData.finalP5.toFixed(2)}%
                </div>
              </div>

              <div className='summary-card normal'>
                <div className='card-header'>
                  <span>سناریوی نرمال (50%)</span>
                  <span className='percent-badge'>نتیجه میانه (Median)</span>
                </div>
                <div className='card-value'>
                  <span className='trend-icon'>📊</span>
                  {chartSimulationData.finalP50.toFixed(2)}%
                </div>
              </div>

              <div className='summary-card good'>
                <div className='card-header'>
                  <span>سناریوی خوشبینانه (95%)</span>
                  <span className='percent-badge'>5% بهترین نتایج</span>
                </div>
                <div className='card-value'>
                  <span className='trend-icon'>🚀</span>
                  {chartSimulationData.finalP95.toFixed(2)}%
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className='plan-recommender-section'>
        <div className='section-header'>
          <h3>پیشنهاد خودکار پلن مدیریت سرمایه</h3>
          <p>فقط سرمایه، بازه زمانی، سطح ریسک و هدف سود را وارد کن؛ سیستم باقی پارامترها را برایت محاسبه می‌کند.</p>
        </div>

        <div className='recommender-box'>
          <div className='rec-inputs'>
            <div className='input-group'>
              <label>بازه زمانی (روز):</label>
              <input
                type='text'
                inputMode='numeric'
                value={recommenderDays}
                onChange={handleInputChange(setRecommenderDays)}
                placeholder='مثلا 90'
              />
            </div>
            <div className='input-group'>
              <label>هدف سود در {recommenderDays} روز (%):</label>
              <input
                type='text'
                inputMode='decimal'
                value={targetProfitPercent}
                onChange={handleInputChange(setTargetProfitPercent)}
                placeholder='مثلا 8'
              />
            </div>
            <div className='input-group'>
              <label>سطح ریسک:</label>
              <select className='custom-select' value={riskLevel} onChange={e => setRiskLevel(e.target.value as any)}>
                <option value='low'>کم (Low)</option>
                <option value='med'>متوسط (Medium)</option>
                <option value='high'>زیاد (High)</option>
              </select>
            </div>
          </div>

          <button className='recommend-btn' onClick={handleRunRecommendation} disabled={isRecommending}>
            {isRecommending ? '... در حال پردازش سناریوها' : 'محاسبه پلن‌های پیشنهادی'}
          </button>
          {recommendationError && <p className='error-message'>{recommendationError}</p>}
        </div>

        {recommendedPlans.length > 0 && (
          <div className='recommendation-results'>
            <div className='best-plan-card text-card'>
              <div className='card-header-text'>
                <strong>جمع‌بندی پیشنهاد اصلی </strong>
              </div>
              <div className='card-body-text'>
                <p>با این تنظیمات:</p>
                <ul>
                  <li>
                    WinRate تقریبی: <b>{(recommendedPlans[0].p * 100).toFixed(0)}%</b>
                  </li>
                  <li>
                    ریوارد به ریسک (RR): <b>{recommendedPlans[0].RR.toFixed(1)}</b>
                  </li>
                  <li>
                    ریسک هر ترید: حدود <b>{(recommendedPlans[0].R * 100).toFixed(2)}%</b> از سرمایه فعلی
                  </li>
                  <li>
                    تعداد معاملات روزانه: حدود <b>{recommendedPlans[0].m}</b> ترید
                  </li>
                </ul>
                <p className='conclusion-text'>
                  اگر سیستم معاملاتی تو بتواند چنین WinRate و RR‌ای تولید کند و به این پلن پایبند بمانی، از نظر آماری
                  شانس رسیدن به حدود <b>{parseFloat(targetProfitPercent).toFixed(1)}%</b> سود در {recommenderDays} روز،
                  حدود <b>{(recommendedPlans[0].probHit * 100).toFixed(1)}%</b> برآورد می‌شود.
                </p>
              </div>
            </div>

            <div className='table-wrapper'>
              <table className='rec-table'>
                <thead>
                  <tr>
                    <th>#</th>
                    <th>WinRate</th>
                    <th>RR</th>
                    <th>ریسک هر ترید</th>
                    <th>تعداد ترید/روز</th>
                    <th>احتمال رسیدن به هدف</th>
                    <th>میانه بازده در {recommenderDays} روز</th>
                    <th>سناریوی بد (۵٪)</th>
                    <th>سناریوی خوب (۹۵٪)</th>
                  </tr>
                </thead>
                <tbody>
                  {recommendedPlans.map(p => (
                    <tr key={p.rank} className={p.rank === 1 ? 'top-row' : ''}>
                      <td>{p.rank}</td>
                      <td>{(p.p * 100).toFixed(0)}%</td>
                      <td>{p.RR.toFixed(1)}</td>
                      <td>{(p.R * 100).toFixed(2)}%</td>
                      <td>{p.m}</td>
                      <td className='highlight-val'>{(p.probHit * 100).toFixed(1)}%</td>
                      <td>{(p.retMed * 100).toFixed(1)}%</td>
                      <td className='warn-val' style={{ direction: 'ltr' }}>
                        {(p.ret5 * 100).toFixed(1)}%
                      </td>
                      <td style={{ direction: 'ltr' }}>{(p.ret95 * 100).toFixed(1)}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className='footnote'>
              🔸 توجه: این اعداد «میانه» و «بازهٔ تقریبی بد/خوب» هستند؛ بر اساس امیدریاضی و رفتار آماری سیستم طراحی
              شده‌اند و وعدهٔ قطعی سود نیستند.
            </p>
          </div>
        )}
      </div>
      <CalculationDetails theme={theme === 'dark' ? 'dark' : 'light'} />
    </div>
  )
}

export default PredictionPage
